// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import setUseBillingAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseBillingAddress";
import setUseOneTimeAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseOneTimeAddress";
import { theme } from "@insite/client-framework/Theme";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import { ShipToSelectorStyles } from "@insite/content-library/Components/ShipToSelector";
import CreateNewAddressModal, {
    CreateNewAddressModalStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/CreateNewAddressModal";
import EditExistingAddressModal, {
    EditExistingAddressModalStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/EditExistingAddressModal";
import ShippingAddressForm from "@insite/content-library/Widgets/CheckoutShipping/ShippingAddressForm";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import Select from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xIsPickup } from "@keller/App/Store/Selectors/GeneralSelectors";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@keller/Overrides/Components/AddressInfoDisplay";
import sharedAddressesStyles from "@keller/Overrides/Widgets/CheckoutShipping/SharedAddresses.style";
import { mergeWithCss } from "@keller/XDL/Utilities";
import React, { FC, PropsWithChildren, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    address?: ShipToModel;
    currentBillToId: string | undefined;
    newAddress?: ShipToModel;
    oneTimeAddress?: ShipToModel;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    // XNG-Changes - Add onSuccess
    // onChange: (address: ShipToModel, isEdit: boolean) => void;
    onChange: (address: ShipToModel, isEdit: boolean, onSuccess?: () => void) => void;
    showUseBillingAddress?: boolean;
    isUseBillingAddressDisabled?: boolean;
    onClickUseBillingAddress?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    extendedStyles?: ShippingAddressStyles;
}

// <XNG-Changes>
const getShipTosParameter = (billToId?: string): GetShipTosApiParameter => ({
    billToId,
    exclude: ["showAll", "billTo", "oneTime", "createNew"],
});
// </XNG-Changes>

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    // <XNG-Changes>
    const shipTosParameter = getShipTosParameter(props.currentBillToId);
    // XNG-Changes
    const { cartId } = getQueryStrings(state) || state.pages.checkoutReviewAndSubmit;
    const { session } = state.context;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const fulfillmentMethod = cart?.fulfillmentMethod || session.fulfillmentMethod;

    // </XNG-Changes>

    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        useBillingAddress: state.pages.checkoutShipping.useBillingAddress,
        useOneTimeAddress: state.pages.checkoutShipping.useOneTimeAddress,
        allowCreateNewShipToAddress: getSettingsCollection(state).customerSettings.allowCreateNewShipToAddress,
        shippingAddressFormState: state.pages.checkoutShipping.shippingAddressFormState,
        isShippingAddressUpdateRequired: state.pages.checkoutShipping.isShippingAddressUpdateRequired,
        // XNG-Changes
        shipTosDataView: getShipTosDataView(state, shipTosParameter),
        shipTosParameter,
        allowOneTimeAddresses: getSettingsCollection(state).customerSettings.allowOneTimeAddresses,
        isPickUp: xIsPickup(fulfillmentMethod),
    };
};

const mapDispatchToProps = {
    setUseBillingAddress,
    setUseOneTimeAddress,
    // XNG-Changes
    loadShipTos,
    setAddressBookOverlayIsOpen,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface ShippingAddressStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    selectSavedAddressLink?: LinkPresentationProps;
    addressBookModal?: ModalPresentationProps;
    shipToSelector?: ShipToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    createNewAddressModal?: CreateNewAddressModalStyles;
    editAddressModal?: EditExistingAddressModalStyles;
    oneTimeAddressGridItem?: GridItemProps;
    oneTimeAddressCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    oneTimeAddressCheckbox?: CheckboxPresentationProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    addressDisplay?: AddressInfoDisplayStyles;
    useBillingAddressButton?: ButtonPresentationProps;
    // xng-changes
    addressFormGridItem?: GridItemProps;
    useOneTimeTitle?: TypographyPresentationProps;
}

export const shippingAddressStyles: ShippingAddressStyles = {
    container: {
        ...sharedAddressesStyles.container,
    },
    headingGridItem: {
        ...sharedAddressesStyles.headingGridItem,
    },
    headingText: {
        ...sharedAddressesStyles.headingText,
    },
    addressDisplayAndFormGridItem: {
        ...sharedAddressesStyles.addressDisplayGridItem,
    },
    addressFormGridItem: {
        ...mergeWithCss(sharedAddressesStyles.addressFormGridItem, {
            css: css`
                flex-direction: column;
            `,
        }),
    },
    oneTimeAddressGridItem: {
        width: 12,
        css: css`
            &&&& {
                margin-bottom: 16px;
            }
        `,
    },
    selectSavedAddressLink: {
        css: css`
            margin-left: 20px;
        `,
    },
    addressBookModal: { sizeVariant: "medium" },
    addressBookModalButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        `,
    },
    addressBookModalCancelButton: { variant: "secondary" },
    useBillingAddressButton: {
        variant: "secondary",
        css: css`
            margin-top: 20px;
        `,
    },
    useOneTimeTitle: {
        variant: "h5",
        css: css`
            margin-bottom: 20px;
        `,
    },
};

// XNG-Changes: replace checkboxes & radio with Select,
// adding LoadingOverlay
// remove the ability to edit the address
// styles changes
const ShippingAddress: FC<PropsWithChildren<Props>> = ({
    address,
    newAddress,
    oneTimeAddress,
    onChange,
    currentUserIsGuest,
    useBillingAddress,
    useOneTimeAddress,
    allowCreateNewShipToAddress,
    setUseBillingAddress,
    setUseOneTimeAddress,
    extendedStyles,
    currentBillToId,
    isUseBillingAddressDisabled,
    shippingAddressFormState,
    showUseBillingAddress,
    isShippingAddressUpdateRequired,
    countries,
    addressFieldDisplayCollection,
    shipTosDataView,
    loadShipTos,
    shipTosParameter,
    allowOneTimeAddresses,
    // XNG-Change
    isPickUp,
}) => {
    const [isCustomerSelectorModalOpen, setIsCustomerSelectorModalOpen] = useState(false);
    const [isCreateNewAddressModalOpen, setIsCreateNewAddressModalOpen] = useState(false);
    const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);
    const [addressBeingEdited, setAddressBeingEdited] = useState<ShipToModel | undefined>(undefined);
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(shippingAddressStyles, extendedStyles));
    // <XNG-Changes>
    const [selectedAddress, setSelectedAddress] = useState(
        shippingAddressFormState && shippingAddressFormState.address.oneTimeAddress
            ? "oneTime"
            : address?.id === currentBillToId
              ? "billing"
              : address?.id,
    );
    useEffect(() => {
        if (!shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos(shipTosParameter);
        }
    });
    useEffect(() => {
        if (!shipTosDataView.value) {
            return;
        }
        if (selectedAddress === "billing" && !isPickUp) {
            setUseBillingAddress({
                useBillingAddress: true,
            });
            return;
        }
        if (selectedAddress === "onetime") {
            setUseOneTimeAddress({
                useOneTimeAddress: true,
            });
            return;
        }

        const shipTo = shipTosDataView.value?.find(d => d.id === selectedAddress);
        if (!shipTo) {
            return;
        }

        onChange(shipTo, false, () => {
            // to make sure that the lastSelectedShipTo has been set correctly
            setUseOneTimeAddress({
                useOneTimeAddress: false,
            });
        });
    }, [selectedAddress]);
    // </XNG-Changes>
    useEffect(() => {
        if (addressBeingEdited) {
            setIsEditAddressModalOpen(true);
        }
    }, [addressBeingEdited]);

    const oneTimeAddressChangeHandler: CheckboxProps["onChange"] = (_, value) =>
        setUseOneTimeAddress({
            useOneTimeAddress: value,
        });

    const useBillingAddressChangeHandler: CheckboxProps["onChange"] = (_, value) =>
        setUseBillingAddress({
            useBillingAddress: value,
        });

    const addressTypeChangeHandler: React.ChangeEventHandler<HTMLSelectElement> = event => {
        // XNG-Changes: set selected address in local state
        setSelectedAddress(event.target.value);
        // if (event.target.value === "onetime") {
        //     setUseOneTimeAddress({
        //         useOneTimeAddress: true,
        //     });
        // } else if (event.target.value === "billing") {
        //     setUseBillingAddress({
        //         useBillingAddress: true,
        //     });
        // }
    };

    const selectAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false })
            : setIsCustomerSelectorModalOpen(true);
    };

    const customerSelectorModalCloseHandler = () => setIsCustomerSelectorModalOpen(false);

    const selectCustomerHandler = (shipTo: ShipToModel) => {
        customerSelectorModalCloseHandler();
        onChange(shipTo, false);
    };

    const createNewAddressHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setIsCreateNewAddressModalOpen(true);
    };

    const createNewAddressModalCloseHandler = () => setIsCreateNewAddressModalOpen(false);

    const newShippingAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        onChange(address, true);
        createNewAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: translate("Address has been created."),
        });
    };

    const editCustomerHandler = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        customer: BaseAddressModel,
    ) => {
        event.preventDefault();
        setAddressBeingEdited(customer as ShipToModel);
    };

    const editAddressModalCloseHandler = () => {
        setIsEditAddressModalOpen(false);
        setAddressBeingEdited(undefined);
    };

    const editAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        onChange(address, true);
        editAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: translate("Address updated."),
        });
    };
    return (
        <GridContainer {...styles.container} data-test-selector="checkoutShipping_shippingAddress">
            <GridItem {...styles.headingGridItem}>
                <Typography {...styles.headingText} as="h3">
                    {translate("Shipping Address")}
                </Typography>

                {/* {(!currentUserIsGuest ||
                         (currentUserIsGuest && (allowCreateNewShipToAddress || !!newAddress || !!address))) && (
                    <Link
                        {...styles.selectSavedAddressLink}
                        type="button"
                        onClick={selectAddressClickHandler}
                        data-test-selector="checkoutShipping_selectSavedAddress"
                    >
                        {translate("Edit")}
                        <VisuallyHidden>{translate("For Shipping Address")}</VisuallyHidden>
                    </Link>
                )}
                <Modal
                    {...styles.addressBookModal}
                    headline={translate("Address Book")}
                    isOpen={isCustomerSelectorModalOpen}
                    handleClose={customerSelectorModalCloseHandler}
                    data-test-selector="checkoutShipping_addressModal"
                >
                    <ShipToSelector
                        currentShipTo={useOneTimeAddress && shippingAddressFormState ? undefined : address}
                        currentBillToId={currentBillToId}
                        newAddress={newAddress}
                        onSelect={selectCustomerHandler}
                        onEdit={editCustomerHandler}
                        onCreateNewAddressClick={newAddress && createNewAddressHandler}
                        extendedStyles={styles.shipToSelector}
                    />
                    <StyledWrapper {...styles.addressBookModalButtonsWrapper}>
                        <Button
                            {...styles.addressBookModalCancelButton}
                            onClick={customerSelectorModalCloseHandler}
                            data-test-selector="addressModal_cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </Modal> */}
                {newAddress && (
                    <CreateNewAddressModal
                        newAddress={newAddress}
                        isModalOpen={isCreateNewAddressModalOpen}
                        onModalClose={createNewAddressModalCloseHandler}
                        onFormSubmit={newShippingAddressFormSubmitHandler}
                    />
                )}
                {addressBeingEdited && (
                    <EditExistingAddressModal
                        newAddress={addressBeingEdited}
                        isModalOpen={isEditAddressModalOpen}
                        onModalClose={editAddressModalCloseHandler}
                        onFormSubmit={editAddressFormSubmitHandler}
                    />
                )}
            </GridItem>
            <GridItem {...styles.oneTimeAddressGridItem}>
                <Select
                    label={translate("Ship-To Address")}
                    // XNG-Change: use id as a value
                    value={selectedAddress}
                    onChange={addressTypeChangeHandler}
                    // XNG-Changes: remove disable on isUseBillingAddressDisabled
                    disabled={currentUserIsGuest}
                >
                    {allowOneTimeAddresses && (
                        <option key="onetime" value="onetime">
                            {translate("Add One Time Shipping Address")}
                        </option>
                    )}
                    {showUseBillingAddress && (
                        <option key="billing" value="billing">
                            {translate("Use Billing Address")}
                        </option>
                    )}
                    {shipTosDataView.value?.map(shipTo => (
                        <option key={shipTo.id} value={shipTo.id}>
                            {shipTo.fullAddress}
                        </option>
                    ))}
                </Select>
            </GridItem>
            {(useOneTimeAddress || currentUserIsGuest || isShippingAddressUpdateRequired) && (
                <GridItem {...styles.addressFormGridItem}>
                    <Typography {...styles.useOneTimeTitle}>{translate("Add One Time Shipping Address")}</Typography>
                    <ShippingAddressForm countries={countries} fieldDisplay={addressFieldDisplayCollection} />
                </GridItem>
            )}
            {!useOneTimeAddress && !currentUserIsGuest && !isShippingAddressUpdateRequired && address && (
                <GridItem {...styles.addressDisplayAndFormGridItem}>
                    <AddressInfoDisplay
                        {...address}
                        state={address.state ? address.state.abbreviation : undefined}
                        country={address.country ? address.country.abbreviation : undefined}
                        extendedStyles={styles.addressDisplay}
                        showDefaultLabel={true}
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(ShippingAddress));
